import { UserRole } from "./roles";

/**
 * Dashboard navigation, resolved per role.
 *
 * One list drives both the sidebar and the route guards in `App.jsx`, so a tab
 * a role cannot see is also a URL it cannot type. Keeping them apart is how you
 * ship a hidden link that still renders its page to anyone who deep-links it.
 *
 * This is presentation only. It decides what a role is *offered*, never what
 * they are *allowed*: the API re-derives scope from the JWT on every request
 * (see `BookingController.GetOwnerBoundary`), because anything localStorage
 * holds is the client's to edit.
 */

/**
 * There are two kinds of signed-in staff here, and they do not share a menu.
 *
 *  - *Platform* staff (SystemOwner, Admin) run EasyPlanner itself. They have no
 *    organization of their own — `User.OrganizationId` is null for them — so
 *    every company-owner tab is either empty or a 403 to them, and the wizard on
 *    /dashboard/organization is a setup flow for a business that will never
 *    exist. They get PLATFORM_NAV, and a SystemOwner gets one tab more —
 *    SYSTEM_OWNER_NAV, which adds Admins.
 *  - *Company* staff (CompanyAdmin, CompanyEmployee) run one business. They get
 *    the tabs that manage it.
 *
 * The two sets are disjoint apart from Support and Settings, which mean the same
 * thing to everyone. That is why "Book" and "My Bookings" are not on the platform
 * menu even though an employee of the platform is also a person who books things:
 * consuming the product is a customer's errand, and a platform account is not
 * the account they'd do it from.
 */

/**
 * Labels are per-role, not global, because the same page means different things
 * to different people:
 *  - Resources is the roster to an admin ("Team") but the employee's own
 *    working hours to an employee ("Availability").
 *  - Calendar is the org-wide schedule to an admin, and only the employee's own
 *    working day to an employee ("My Schedule") — the API already narrows it.
 *
 * "My Bookings" means the same thing for everyone: appointments *I* booked as a
 * customer. Every role gets it, because an admin and an employee are also people
 * who book things — an employee's work calendar is a different list and is named
 * "My Schedule" for exactly that reason.
 *
 * "Book" is the other half of that pair and follows the same rule: it is where
 * anyone signed in searches the platform and books for themselves. It sits
 * directly above My Bookings everywhere, because finding an appointment and
 * having one are two steps of the same errand.
 */
const ITEMS = {
  organization: {
    id: "organization",
    path: "/dashboard/organization",
    icon: "bi-building",
    labelKey: "Dashboard.Menu.organization",
  },
  reports: {
    id: "reports",
    path: "/dashboard/reports",
    icon: "bi-bar-chart-line",
    labelKey: "Dashboard.Menu.reports",
  },
  calendar: {
    id: "calendar",
    path: "/dashboard/calendar",
    icon: "bi-calendar3",
    labelKey: "Dashboard.Menu.calendar",
  },
  bookings: {
    id: "bookings",
    path: "/dashboard/bookings",
    icon: "bi-calendar-check",
    labelKey: "Dashboard.Menu.bookings",
  },
  services: {
    id: "services",
    path: "/dashboard/services",
    icon: "bi-briefcase",
    labelKey: "Dashboard.Menu.services",
  },
  resources: {
    id: "resources",
    path: "/dashboard/resources",
    icon: "bi-people",
    labelKey: "Dashboard.Menu.resources",
  },
  site: {
    id: "site",
    path: "/dashboard/site",
    icon: "bi-globe2",
    labelKey: "Dashboard.Menu.mySite",
  },
  settings: {
    id: "settings",
    path: "/dashboard/settings",
    icon: "bi-gear",
    labelKey: "Dashboard.Menu.settings",
  },
  overview: {
    id: "overview",
    path: "/dashboard/overview",
    icon: "bi-grid-1x2",
    labelKey: "Dashboard.Menu.overview",
  },
  book: {
    id: "book",
    path: "/dashboard/book",
    icon: "bi-search",
    labelKey: "Dashboard.Menu.book",
  },
  myBookings: {
    id: "myBookings",
    path: "/dashboard/my-bookings",
    icon: "bi-calendar-heart",
    labelKey: "Dashboard.Menu.myBookings",
  },
  /**
   * What the business pays, what it has paid, and its invoices.
   *
   * CompanyAdmin only, and deliberately not on the employee menu: the plan
   * panel used to sit in Settings, where an employee could read the price their
   * employer pays. It is also not setup-gated — an owner mid-wizard can still
   * reach it, because the one thing a product must never block is somebody
   * trying to pay for it.
   */
  billing: {
    id: "billing",
    path: "/dashboard/billing",
    icon: "bi-credit-card",
    labelKey: "Dashboard.Menu.billing",
  },
  support: {
    id: "support",
    path: "/dashboard/support",
    icon: "bi-life-preserver",
    labelKey: "Dashboard.Menu.support",
  },

  // --- Platform-staff items -------------------------------------------------

  platform: {
    id: "platform",
    path: "/dashboard/platform",
    icon: "bi-speedometer2",
    labelKey: "Dashboard.Menu.platform",
  },
  companies: {
    id: "companies",
    path: "/dashboard/companies",
    icon: "bi-buildings",
    labelKey: "Dashboard.Menu.companies",
  },
  invitations: {
    id: "invitations",
    path: "/dashboard/invitations",
    icon: "bi-envelope-plus",
    labelKey: "Dashboard.Menu.invitations",
  },
  admins: {
    id: "admins",
    path: "/dashboard/admins",
    icon: "bi-person-badge",
    labelKey: "Dashboard.Menu.admins",
  },
  pricing: {
    id: "pricing",
    path: "/dashboard/pricing",
    icon: "bi-tags",
    labelKey: "Dashboard.Menu.pricing",
  },
};

const withLabel = (item, labelKey) => ({ ...item, labelKey });

/**
 * The tabs both kinds of platform staff get. What differs between them is how
 * much each tab shows: the API narrows every one of these to the organizations
 * the caller may see — all of them for a SystemOwner, only those with
 * `Organization.AssignedAdminId = me` for an Admin — so the menu does not have
 * to know the difference, and cannot get it wrong.
 */
const PLATFORM_NAV = [
  ITEMS.platform,
  ITEMS.companies,
  ITEMS.invitations,
  ITEMS.support,
  ITEMS.settings,
];

/**
 * The two tabs a SystemOwner has and an Admin does not.
 *
 * Neither is a narrowing of a shared view like the others, and both are held
 * back for the same reason — they decide something about the platform itself
 * rather than about one business on it:
 *
 *  - Admins is where the `Admin` role is handed out, so an Admin who could open
 *    it could mint peers and quietly widen who runs the platform.
 *  - Pricing is what EasyPlanner charges. An Admin looks after the companies
 *    assigned to them; the price list is not theirs to move, and a 100%
 *    promotion is an off switch for the revenue of the whole business.
 *
 * Kept as routes rather than sections inside another tab for the usual reason
 * (see the file header): a role that cannot see the tab cannot type the URL
 * either. `AdminInviteController` and `PlatformPricingController` are both
 * SystemOwner-only server-side, and their services re-check — this half is UX.
 */
const SYSTEM_OWNER_NAV = [
  ITEMS.platform,
  ITEMS.companies,
  ITEMS.invitations,
  ITEMS.admins,
  ITEMS.pricing,
  ITEMS.support,
  ITEMS.settings,
];

const COMPANY_ADMIN_NAV = [
  ITEMS.organization,
  ITEMS.reports,
  ITEMS.calendar,
  ITEMS.bookings,
  ITEMS.services,
  withLabel(ITEMS.resources, "Dashboard.Menu.team"),
  ITEMS.site,
  ITEMS.book,
  ITEMS.myBookings,
  ITEMS.billing,
  ITEMS.support,
  ITEMS.settings,
];

const EMPLOYEE_NAV = [
  withLabel(ITEMS.calendar, "Dashboard.Menu.mySchedule"),
  ITEMS.bookings,
  withLabel(ITEMS.resources, "Dashboard.Menu.availability"),
  ITEMS.book,
  ITEMS.myBookings,
  ITEMS.support,
  ITEMS.settings,
];

const CUSTOMER_NAV = [
  ITEMS.overview,
  ITEMS.book,
  ITEMS.myBookings,
  ITEMS.settings,
];

/**
 * What a SystemOwner gets while simulating a company owner ("open as owner" on
 * the Companies tab): the parts of the business they set up on the owner's
 * behalf, and nothing that is the owner's personally.
 *
 * The token is a CompanyAdmin's, so without this the whole company menu would
 * appear — billing, settings, "My Bookings" — and every one of those tabs is
 * either refused by the API's simulation fence (`ImpersonationPolicy`) or none
 * of platform staff's business. Organization comes first because it is where
 * the setup wizard lives, and setting a business up is what a simulation is
 * for. Setup gating still applies: the simulation sees exactly what the owner
 * would.
 */
const IMPERSONATION_NAV = [
  ITEMS.organization,
  ITEMS.calendar,
  ITEMS.bookings,
  ITEMS.services,
  withLabel(ITEMS.resources, "Dashboard.Menu.team"),
  ITEMS.site,
];

const NAV_BY_ROLE = {
  [UserRole.SYSTEM_OWNER]: SYSTEM_OWNER_NAV,
  [UserRole.ADMIN]: PLATFORM_NAV,
  [UserRole.COMPANY_ADMIN]: COMPANY_ADMIN_NAV,
  [UserRole.COMPANY_EMPLOYEE]: EMPLOYEE_NAV,
  [UserRole.USER]: CUSTOMER_NAV,
};

/**
 * The sidebar items for a role. An unknown or missing role gets the customer
 * menu — the least privileged one — so a token we cannot read never opens more
 * than it should.
 *
 * `impersonating` (a simulation token, see `IMPERSONATION_NAV`) replaces the
 * role's menu outright rather than narrowing it, so a simulation can never be
 * offered more than those tabs whatever role its token carries.
 */
export const getNavItemsForRole = (role, { impersonating = false } = {}) =>
  impersonating ? IMPERSONATION_NAV : (NAV_BY_ROLE[role] ?? CUSTOMER_NAV);

/**
 * Where a role lands after login, and where a bad URL sends them. Each role's
 * first nav item is its home by construction, so this cannot drift out of sync
 * with the menu.
 */
export const getDefaultRouteForRole = (role, options) =>
  getNavItemsForRole(role, options)[0].path;

/**
 * Whether a role may open a dashboard path. Compared by prefix so nested routes
 * (`/dashboard/bookings/42`) inherit their parent's answer.
 */
export const canAccessPath = (role, path, options) =>
  getNavItemsForRole(role, options).some(
    (item) => path === item.path || path.startsWith(`${item.path}/`),
  );

/**
 * Whether `role` runs the platform rather than a business on it. These accounts
 * have no organization at all, so anything that reads one — the setup wizard,
 * the plan panel, the org notification preferences — has nothing to read and
 * should not be offered to them.
 */
export const isPlatformRole = (role) =>
  role === UserRole.SYSTEM_OWNER || role === UserRole.ADMIN;

/**
 * Whether `role` can own an organization — the only role an incomplete
 * organization setup can ever gate.
 *
 * Only CompanyAdmin. SystemOwner and Admin used to be here, back when they
 * shared the company menu; they never actually owned an organization, so the
 * gate could only ever have locked tabs they had no business seeing in the
 * first place. Now that they have their own menu, including them would gate
 * paths that are not on it.
 *
 * CompanyEmployee and User are likewise never gated: they are always treated as
 * `isSetupComplete = true` regardless of the organization's actual state,
 * because finishing setup is an owner's job, not theirs.
 */
export const isOrgOwnerCapableRole = (role) => role === UserRole.COMPANY_ADMIN;

/**
 * The nav item ids that stay visible but non-interactive — in the sidebar and
 * as a direct URL — until the organization's setup wizard is complete.
 * Deliberately excludes `organization` (the wizard's own home), `settings`
 * and `support` (always reachable so a stuck owner can still get help).
 */
export const GATED_SETUP_ITEM_IDS = new Set([
  "reports",
  "calendar",
  "bookings",
  "services",
  "resources", // "Team" to an admin
  "site", // "My Site"
  "book",
  "myBookings",
]);

/**
 * Whether `path` is one of the setup-gated items for `role`. Always false for
 * a role that isn't org-owner-capable, and false for any admin path that
 * isn't in the gated set (Organization itself, Settings, Support).
 */
export const isSetupGatedPath = (role, path, options) => {
  if (!isOrgOwnerCapableRole(role)) return false;
  const item = getNavItemsForRole(role, options).find(
    (i) => path === i.path || path.startsWith(`${i.path}/`),
  );
  return item ? GATED_SETUP_ITEM_IDS.has(item.id) : false;
};
